// Students on a reviewed sort who have no row in the assessment table.
// One entry per roll: three sheets of (A48) stay a single late paper.
// A student who was already checked is listed too when this folder both
// deleted sheets and added sheets, and the new sheet numbers are on that
// roll. The old checked row does not hide them. Their entry is the current
// sort, the fresh sheets, counted the same way as a never-checked student.

const REVIEWED_STATUSES = new Set(['reviewed', 'auto-checked', 'copy_to_edupage', 'completed']);
const BAD_ROLLS = new Set(['', '000', '0000', '0']);

export function rollFromLabel(value) {
  const match = String(value || '').match(/\(([^)]+)\)/);
  return match ? match[1].trim().toLowerCase() : '';
}

function rollOfRow(row) {
  const code = String((row && row.unique_code) || '').trim().toLowerCase();
  if (code && !BAD_ROLLS.has(code)) return code;
  return rollFromLabel(row && row.roll_name);
}

function imageNumber(value) {
  const text = String(value || '');
  const named = text.match(/Image_(\d+)/i);
  if (named) return named[1];
  const bare = text.trim();
  return /^\d+$/.test(bare) ? bare : '';
}

function numberSet(values) {
  const out = new Set();
  for (const value of values || []) {
    const n = imageNumber(value);
    if (n) out.add(n);
  }
  return out;
}

export function listLatePapers(snapshotRows, assessmentRows, changes) {
  const rows = (Array.isArray(snapshotRows) ? snapshotRows : [])
    .filter((row) => REVIEWED_STATUSES.has(String((row && row.status) || '').trim().toLowerCase()));
  if (!rows.length) return [];

  const assessed = new Set();
  for (const row of assessmentRows || []) {
    const roll = rollFromLabel(row && row.student_name);
    if (roll && !BAD_ROLLS.has(roll)) assessed.add(roll);
  }
  const added = numberSet(changes && changes.added);
  const deleted = numberSet(changes && changes.deleted);
  // Both sides of a swap. Added-only or deleted-only does not pull a
  // checked student back onto the list.
  const swap = added.size > 0 && deleted.size > 0;

  const byRoll = new Map();
  for (const row of rows) {
    const roll = rollOfRow(row);
    if (!roll || BAD_ROLLS.has(roll)) continue;
    if (assessed.has(roll) && !swap) continue;
    const named = String((row && row.roll_name) || '').trim();
    let entry = byRoll.get(roll);
    if (!entry) {
      entry = {
        roll,
        name: rollFromLabel(named) === roll ? named : `(${roll.toUpperCase()})`,
        images: []
      };
      byRoll.set(roll, entry);
    } else if (rollFromLabel(named) === roll && named.length > entry.name.length) {
      entry.name = named;
    }
    const image = String((row && row.image_no) || '').trim();
    if (image && !entry.images.includes(image)) entry.images.push(image);
    if (assessed.has(roll)) entry.checked = true;
  }

  return [...byRoll.values()].filter((entry) => {
    if (!entry.checked) return true;
    return entry.images.some((image) => added.has(imageNumber(image)));
  }).map(({ checked, ...entry }) => entry).sort((a, b) => String(a.name).localeCompare(
    String(b.name),
    undefined,
    { numeric: true, sensitivity: 'base' }
  ));
}

// The check window stays closed when a late paper cannot be opened.
// Each reason keeps the server message's meaning and names the next step.
export function latePaperOpenFailure(notice, studentName) {
  const who = String(studentName || '').trim() || 'This student';
  const text = String(notice || '').trim() || 'The check window could not be opened.';
  if (/no sheet images/i.test(text)) {
    return {
      title: 'Late paper did not open',
      message: `${who} has no sheet image on the reviewed list, so the check window has nothing to open.`,
      suggestion: 'Open Review for this folder, put the sheet image on this student\'s row, and save the review. Then click Late papers again.'
    };
  }
  if (/no roll number/i.test(text) || /folder and roll are required/i.test(text)) {
    return {
      title: 'Late paper did not open',
      message: `${who} has no roll number, so the paper cannot be matched to a sheet.`,
      suggestion: 'Set the roll number on the reviewed list, save the review, then click Late papers again.'
    };
  }
  if (/no reviewed list/i.test(text)) {
    return {
      title: 'Late paper did not open',
      message: 'This folder has no reviewed list, so the late paper cannot be opened.',
      suggestion: 'Open Review, save the sorted papers, then click Late papers again.'
    };
  }
  if (/not on the reviewed list/i.test(text)) {
    return {
      title: 'Late paper did not open',
      message: `${who} is not on the reviewed list.`,
      suggestion: 'Open Review, save this student on the reviewed list, then click Late papers again.'
    };
  }
  return {
    title: 'Late paper did not open',
    message: text,
    suggestion: 'Open this class with Re-check and look at this student\'s row. If the Image column is empty, attach the sheet in Review and click Late papers again.'
  };
}
